import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Group,
  Modal,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useConsentTypes } from '../api/consent';
import { useOrgs } from '../api/orgs';
import { useProducts } from '../api/products';
import { useQuestionSet } from '../api/question-sets';
import {
  useCloneWorkflow,
  useDeleteWorkflow,
  useNewVersionWorkflow,
  useWorkflow,
  type CollectImagesStep,
  type CollectQuestionsStep,
  type OutputStep,
  type WorkflowDefinition,
  type WorkflowDefinitionGraph,
  type WorkflowStep,
} from '../api/workflows';
import { WorkflowFlowView } from '../components/workflow-flow';

const STATUS_COLOURS: Record<string, string> = {
  draft: 'yellow',
  active: 'green',
  archived: 'gray',
};

function isCollectQuestions(s: WorkflowStep): s is CollectQuestionsStep {
  return s.kind === 'collect_questions';
}

/**
 * Renders a link to a question set with its human-readable name. Used
 * in both the step list and the endpoint preview so operators see the
 * set's title instead of a raw UUID. Falls back to a clear missing-id
 * message when the workflow step has no question_set_id wired up yet
 * (legacy data from before Phase 5c).
 */
function QuestionSetLink({ id }: { id: string | undefined }) {
  // Skip the fetch when there's no id to look up — gives us a clean
  // "not assigned yet" hint without hitting 404.
  const query = useQuestionSet(id);
  if (!id) {
    return (
      <Text component="span" c="red" size="sm" fs="italic">
        no question set assigned
      </Text>
    );
  }
  const label = query.data?.name ?? (query.isPending ? id : id);
  return (
    <Anchor component={Link} to={`/question-sets/${id}`}>
      {label}
    </Anchor>
  );
}
function isCollectImages(s: WorkflowStep): s is CollectImagesStep {
  return s.kind === 'collect_images';
}
function isOutput(s: WorkflowStep): s is OutputStep {
  return s.kind === 'output';
}

/**
 * Endpoint preview. Phase 2 keeps this read-only and informational —
 * we surface the URL a client product would integrate against to fetch
 * the definition, and a JSON-schema-ish preview of the payload shape
 * each `collect_*` step expects (Phase 3 lifts this to a fully
 * runnable contract). The intent is "an integrator can see at a glance
 * what they'd need to wire up" without leaving the admin UI.
 */
function EndpointPreview({ workflow }: { workflow: WorkflowDefinition }) {
  const graph = workflow.definition as WorkflowDefinitionGraph | null;
  return (
    <Stack gap="md">
      <div>
        <Text fw={600} mb={4}>
          Fetch the definition
        </Text>
        <Code block>
          GET /v1/workflow-definitions/{workflow.id}
          {'\n'}Authorization: Bearer &lt;product-jwt&gt;
        </Code>
      </div>

      {graph?.steps && (
        <div>
          <Text fw={600} mb={4}>
            Per-step payload shapes
          </Text>
          <Stack gap="xs">
            {Object.entries(graph.steps).map(([id, step]) => {
              if (isCollectQuestions(step)) {
                // Per-question payload shape now depends on the
                // referenced question set; rendering a static preview
                // would require fetching every set. Show the
                // reference and a link to the set's edit page so the
                // operator can see its questions there.
                return (
                  <div key={id}>
                    <Text size="sm" c="dimmed">
                      <Code>{id}</Code> answers payload: derived from question set{' '}
                      <QuestionSetLink id={step.params.question_set_id} />
                    </Text>
                  </div>
                );
              }
              if (isCollectImages(step)) {
                return (
                  <div key={id}>
                    <Text size="sm" c="dimmed">
                      <Code>{id}</Code> image upload (one of):
                    </Text>
                    <Code block>
                      {JSON.stringify({ configuration: step.params.configurations }, null, 2)}
                    </Code>
                  </div>
                );
              }
              if (isOutput(step)) {
                const shape: Record<string, unknown> = {};
                for (const f of step.params.fields) {
                  shape[f.name] = `<${f.type}>`;
                }
                return (
                  <div key={id}>
                    <Text size="sm" c="dimmed">
                      <Code>{id}</Code> result the client receives:
                    </Text>
                    <Code block>{JSON.stringify(shape, null, 2)}</Code>
                  </div>
                );
              }
              return null;
            })}
          </Stack>
        </div>
      )}
    </Stack>
  );
}

export function WorkflowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useWorkflow(id);
  // Pulled at the page level so the "Org / Product" card can show
  // names instead of UUIDs. Cache hits via React Query — these
  // queries are shared with the workflow list / new modal.
  const orgs = useOrgs();
  const products = useProducts();
  const consentTypes = useConsentTypes(query.data?.productId ?? undefined);
  const consentBlockingByCode = useMemo(
    () => new Map((consentTypes.data ?? []).map((c) => [c.code, c.blocking])),
    [consentTypes.data],
  );

  if (query.isPending) {
    return (
      <Center mih={300}>
        <Text c="dimmed">Loading…</Text>
      </Center>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Could not load workflow.
      </Alert>
    );
  }
  const w = query.data;
  const graph = w.definition as WorkflowDefinitionGraph | null;

  return (
    <Stack gap="lg">
      <Anchor component={Link} to="/workflows" size="sm">
        ← Back to workflows
      </Anchor>

      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Title order={2}>{w.name}</Title>
          {w.description && (
            <Text c="dimmed" size="sm">
              {w.description}
            </Text>
          )}
        </div>
        <Group gap="xs">
          <Badge color={STATUS_COLOURS[w.status] ?? 'gray'} variant="light">
            {w.status}
          </Badge>
          <Badge variant="outline">v{w.version}</Badge>
          <Badge variant="outline">{w.defaultMode}</Badge>
          <WorkflowActions workflow={w} />
        </Group>
      </Group>

      <Card withBorder padding="md">
        <Group gap="xl" wrap="wrap">
          <div>
            <Text size="xs" c="dimmed">
              Org / Product
            </Text>
            <Text size="sm">
              {w.orgId === null
                ? w.isTemplate
                  ? 'Template'
                  : 'Default — all orgs'
                : ((orgs.data ?? []).find((o) => o.id === w.orgId)?.name ?? w.orgId)}{' '}
              /{' '}
              {(products.data ?? []).find((p) => p.id === w.productId)?.displayName ??
                w.productId ??
                '—'}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">
              Workflow timeout
            </Text>
            <Text size="sm">{w.workflowTimeoutSeconds}s</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">
              Created
            </Text>
            <Text size="sm">{new Date(w.createdAt).toLocaleString()}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">
              Published
            </Text>
            <Text size="sm">{w.publishedAt ? new Date(w.publishedAt).toLocaleString() : '—'}</Text>
          </div>
        </Group>
      </Card>

      <div>
        <Title order={4} mb="sm">
          Steps
        </Title>
        {graph?.steps ? (
          <WorkflowFlowView definition={graph} consentBlockingByCode={consentBlockingByCode} />
        ) : (
          <Text c="dimmed">No steps defined.</Text>
        )}
      </div>

      <div>
        <Title order={4} mb="sm">
          Endpoint preview
        </Title>
        <EndpointPreview workflow={w} />
      </div>

      <WorkflowDangerZone workflow={w} />
    </Stack>
  );
}

/**
 * Actions cluster on the detail-page header. The buttons surface
 * conditionally based on status:
 *
 *   - draft     → Edit (jumps into the canvas), Clone…
 *   - active    → Edit as new version, Clone…
 *   - archived  → Clone…
 *
 * Edit-as-new-version routes through the new-version endpoint —
 * published rows are immutable, so editing them means minting a
 * fresh draft at the next version under the same (orgId, productId,
 * name). Clone supports cross-tenant via the modal's org dropdown.
 */
function WorkflowActions({ workflow }: { workflow: WorkflowDefinition }) {
  const navigate = useNavigate();
  const [cloneOpen, setCloneOpen] = useState(false);
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const newVersion = useNewVersionWorkflow(workflow.id);

  function handleNewVersionConfirm() {
    newVersion.mutate(undefined, {
      onSuccess: (created) => {
        notifications.show({
          color: 'green',
          message: `New draft v${created.version} created.`,
        });
        navigate(`/workflows/${created.id}/edit`);
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : 'Could not create new version';
        notifications.show({ color: 'red', message: msg });
        setNewVersionOpen(false);
      },
    });
  }

  return (
    <>
      {workflow.status === 'draft' && (
        <Button component={Link} to={`/workflows/${workflow.id}/edit`} variant="default" size="sm">
          Edit
        </Button>
      )}
      {workflow.status === 'active' && (
        <Button
          variant="default"
          size="sm"
          onClick={() => setNewVersionOpen(true)}
          loading={newVersion.isPending}
        >
          Edit as new version
        </Button>
      )}
      <Button variant="default" size="sm" onClick={() => setCloneOpen(true)}>
        Clone…
      </Button>

      <CloneModal
        opened={cloneOpen}
        onClose={() => setCloneOpen(false)}
        sourceWorkflow={workflow}
      />

      <Modal
        opened={newVersionOpen}
        onClose={() => setNewVersionOpen(false)}
        title="Edit as new version"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Published definitions are immutable. This will create a new draft version{' '}
            <Code>v{workflow.version + 1}</Code> under the same org + product + name with a copy of
            the current definition. You&apos;ll be able to edit and publish the new draft
            independently.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setNewVersionOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleNewVersionConfirm} loading={newVersion.isPending}>
              Create new draft
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

/**
 * Bottom-of-page Danger zone block. Matches the products + orgs detail
 * pages so destructive actions sit in a consistent location across the
 * admin app. The Delete behaviour is soft- or hard-delete depending on
 * whether the workflow has been used — explained in the confirmation
 * modal so the operator knows what they're agreeing to.
 */
function WorkflowDangerZone({ workflow }: { workflow: WorkflowDefinition }) {
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const remove = useDeleteWorkflow(workflow.id);

  function handleDeleteConfirm() {
    remove.mutate(undefined, {
      onSuccess: (res) => {
        notifications.show({
          color: 'green',
          message: res.hardDeleted
            ? 'Workflow deleted.'
            : 'Workflow hidden. History retained because it has been used.',
        });
        navigate('/workflows');
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : 'Could not delete workflow';
        notifications.show({ color: 'red', message: msg });
        setDeleteOpen(false);
      },
    });
  }

  return (
    <>
      <Card withBorder padding="lg" maw={680}>
        <Stack gap="sm">
          <Title order={5}>Danger zone</Title>
          <Group>
            <Button color="red" variant="subtle" onClick={() => setDeleteOpen(true)}>
              Delete workflow
            </Button>
          </Group>
        </Stack>
      </Card>

      <Modal
        opened={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete workflow"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Delete <strong>{workflow.name}</strong> v{workflow.version}?
          </Text>
          <Text size="sm" c="dimmed">
            If the workflow has never been used, it&apos;s removed entirely. If it has been used,
            it&apos;s hidden from the list but its history is retained so existing instances and
            audit records keep working.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteConfirm} loading={remove.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

function CloneModal({
  opened,
  onClose,
  sourceWorkflow,
}: {
  opened: boolean;
  onClose: () => void;
  sourceWorkflow: WorkflowDefinition;
}) {
  const navigate = useNavigate();
  const orgs = useOrgs();
  const products = useProducts();
  const clone = useCloneWorkflow(sourceWorkflow.id);

  const [name, setName] = useState(`Copy of ${sourceWorkflow.name}`);
  const [orgId, setOrgId] = useState(sourceWorkflow.orgId ?? '');
  const [productId, setProductId] = useState(sourceWorkflow.productId ?? '');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    clone.mutate(
      {
        name: name.trim(),
        ...(orgId ? { org_id: orgId } : {}),
        ...(productId ? { product_id: productId } : {}),
      },
      {
        onSuccess: (created) => {
          notifications.show({ color: 'green', message: 'Cloned.' });
          onClose();
          navigate(`/workflows/${created.id}/edit`);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            const parsed = (() => {
              try {
                return JSON.parse(err.body) as { message?: string };
              } catch {
                return null;
              }
            })();
            setError(parsed?.message ?? `Clone failed (${err.status})`);
            return;
          }
          setError(err instanceof Error ? err.message : 'Clone failed');
        },
      },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Clone workflow" size="md">
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {error}
            </Alert>
          )}
          <TextInput
            label="Name"
            description="Defaults to “Copy of …” — change if cloning into the same product."
            required
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <NativeSelect
            label="Target organisation"
            description="Defaults to the source's org. Pick another to lift the workflow into a different tenant on the same product."
            value={orgId}
            onChange={(e) => setOrgId(e.currentTarget.value)}
            data={[
              { value: '', label: "(actor's org)" },
              ...(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name })),
            ]}
          />
          <NativeSelect
            label="Target product"
            description="Defaults to the source's product."
            value={productId}
            onChange={(e) => setProductId(e.currentTarget.value)}
            data={[
              { value: '', label: '(none)' },
              ...(products.data ?? []).map((p) => ({ value: p.id, label: p.displayName })),
            ]}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={clone.isPending}>
              Clone
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
